import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { 
  Shield, ArrowLeft, Search, Filter, RefreshCw, Eye, 
  CheckCircle, XCircle, Clock, AlertTriangle, DollarSign,
  User, Calendar, FileText, MessageSquare
} from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Textarea } from '../../components/ui/textarea';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '../../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import AdminSidebar from '../../components/admin/AdminSidebar';
import AdminMobileNav from '../../components/admin/AdminMobileNav';

const API = window.location.origin + '/api';

export default function InsuranceClaims() {
  const navigate = useNavigate();
  const [claims, setClaims] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [selectedClaim, setSelectedClaim] = useState(null);
  const [showDetailDialog, setShowDetailDialog] = useState(false);
  const [adminNotes, setAdminNotes] = useState('');
  const [approvedAmount, setApprovedAmount] = useState('');
  const [isUpdating, setIsUpdating] = useState(false);

  const fetchClaims = useCallback(async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/admin/insurance/claims`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setClaims(response.data.claims || []);
    } catch (error) {
      toast.error('Failed to fetch insurance claims');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchClaims();
  }, [fetchClaims]);

  const handleUpdateStatus = async (claimId, newStatus) => {
    setIsUpdating(true);
    try {
      const token = localStorage.getItem('token');
      const params = new URLSearchParams({ status: newStatus });
      if (adminNotes) params.append('admin_notes', adminNotes);
      if (newStatus === 'approved' && approvedAmount) {
        params.append('approved_amount', approvedAmount);
      }
      
      await axios.put(`${API}/admin/insurance/claims/${claimId}?${params.toString()}`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast.success(`Claim ${newStatus} successfully`);
      setShowDetailDialog(false);
      setAdminNotes('');
      setApprovedAmount('');
      fetchClaims();
    } catch (error) {
      toast.error('Failed to update claim: ' + (error.response?.data?.detail || error.message));
    } finally {
      setIsUpdating(false);
    }
  };

  const getStatusBadge = (status) => {
    const styles = {
      pending: 'bg-amber-100 text-amber-800 border-amber-200',
      under_review: 'bg-blue-100 text-blue-800 border-blue-200',
      approved: 'bg-green-100 text-green-800 border-green-200',
      rejected: 'bg-red-100 text-red-800 border-red-200'
    };
    const icons = {
      pending: <Clock className="w-3 h-3" />,
      under_review: <Eye className="w-3 h-3" />,
      approved: <CheckCircle className="w-3 h-3" />,
      rejected: <XCircle className="w-3 h-3" />
    };
    return (
      <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium border ${styles[status] || styles.pending}`}>
        {icons[status]}
        {status?.replace('_', ' ').toUpperCase()}
      </span>
    );
  };

  const getClaimTypeBadge = (type) => {
    const styles = {
      property_damage: 'bg-red-50 text-red-700',
      incomplete_service: 'bg-orange-50 text-orange-700',
      provider_no_show: 'bg-purple-50 text-purple-700',
      quality_issue: 'bg-yellow-50 text-yellow-700',
      other: 'bg-gray-50 text-gray-700'
    };
    return (
      <span className={`px-2 py-0.5 rounded text-xs font-medium ${styles[type] || styles.other}`}>
        {type?.replace('_', ' ')}
      </span>
    );
  };

  const filteredClaims = claims.filter(claim => {
    const matchesSearch = 
      claim.customer_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      claim.booking_id?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      claim.id?.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesStatus = statusFilter === 'all' || claim.status === statusFilter;
    return matchesSearch && matchesStatus;
  });

  const stats = {
    total: claims.length,
    pending: claims.filter(c => c.status === 'pending').length,
    under_review: claims.filter(c => c.status === 'under_review').length,
    approved: claims.filter(c => c.status === 'approved').length,
    rejected: claims.filter(c => c.status === 'rejected').length,
    total_claimed: claims.reduce((sum, c) => sum + (c.estimated_damage || 0), 0),
    total_approved: claims.filter(c => c.status === 'approved').reduce((sum, c) => sum + (c.approved_amount || 0), 0)
  };

  return (
    <div className="min-h-screen bg-stone-50 flex">
      <AdminSidebar />
      <AdminMobileNav />
      <main className="flex-1 md:ml-64 overflow-x-hidden pt-16 md:pt-0">
        {/* Header */}
        <header className="bg-white border-b border-slate-200 sticky top-0 z-10">
          <div className="px-4 md:px-8 py-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-4">
                <button onClick={() => navigate('/admin')} className="p-2 hover:bg-slate-100 rounded-lg md:hidden" data-testid="back-btn">
                  <ArrowLeft className="w-5 h-5 text-slate-600" />
                </button>
              <div>
                <h1 className="text-xl font-bold text-slate-900 flex items-center gap-2">
                  <Shield className="w-6 h-6 text-blue-600" />
                  Insurance Claims
                </h1>
                <p className="text-sm text-slate-500">Manage customer insurance claims</p>
              </div>
            </div>
            <Button onClick={fetchClaims} variant="outline" className="gap-2" data-testid="refresh-btn">
              <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
              Refresh
            </Button>
          </div>
        </div>
      </header>

        {/* Main Content */}
        <div className="px-4 md:px-8 py-6 space-y-6">
        {/* Stats Cards */}
        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-4">
          <div className="bg-white rounded-xl p-4 border border-slate-200">
            <p className="text-xs text-slate-500 uppercase">Total Claims</p>
            <p className="text-2xl font-bold text-slate-900">{stats.total}</p>
          </div>
          <div className="bg-amber-50 rounded-xl p-4 border border-amber-200">
            <p className="text-xs text-amber-600 uppercase">Pending</p>
            <p className="text-2xl font-bold text-amber-700">{stats.pending}</p>
          </div>
          <div className="bg-blue-50 rounded-xl p-4 border border-blue-200">
            <p className="text-xs text-blue-600 uppercase">Under Review</p>
            <p className="text-2xl font-bold text-blue-700">{stats.under_review}</p>
          </div>
          <div className="bg-green-50 rounded-xl p-4 border border-green-200">
            <p className="text-xs text-green-600 uppercase">Approved</p>
            <p className="text-2xl font-bold text-green-700">{stats.approved}</p>
          </div>
          <div className="bg-red-50 rounded-xl p-4 border border-red-200">
            <p className="text-xs text-red-600 uppercase">Rejected</p>
            <p className="text-2xl font-bold text-red-700">{stats.rejected}</p>
          </div>
          <div className="bg-purple-50 rounded-xl p-4 border border-purple-200">
            <p className="text-xs text-purple-600 uppercase">Total Claimed</p>
            <p className="text-2xl font-bold text-purple-700">₱{stats.total_claimed.toLocaleString()}</p>
          </div>
          <div className="bg-emerald-50 rounded-xl p-4 border border-emerald-200">
            <p className="text-xs text-emerald-600 uppercase">Total Approved</p>
            <p className="text-2xl font-bold text-emerald-700">₱{stats.total_approved.toLocaleString()}</p>
          </div>
        </div>

        {/* Filters */}
        <div className="bg-white rounded-xl p-4 border border-slate-200">
          <div className="flex flex-col md:flex-row gap-4">
            <div className="flex-1 relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              <Input
                placeholder="Search by customer, booking ID..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10"
                data-testid="search-input"
              />
            </div>
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger className="w-full md:w-48" data-testid="status-filter">
                <Filter className="w-4 h-4 mr-2" />
                <SelectValue placeholder="Filter by status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Status</SelectItem>
                <SelectItem value="pending">Pending</SelectItem>
                <SelectItem value="under_review">Under Review</SelectItem>
                <SelectItem value="approved">Approved</SelectItem>
                <SelectItem value="rejected">Rejected</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>

        {/* Claims Table */}
        <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
          {loading ? (
            <div className="p-12 text-center">
              <RefreshCw className="w-8 h-8 animate-spin mx-auto text-blue-600" />
              <p className="mt-2 text-slate-500">Loading claims...</p>
            </div>
          ) : filteredClaims.length === 0 ? (
            <div className="p-12 text-center">
              <Shield className="w-12 h-12 mx-auto text-slate-300" />
              <p className="mt-2 text-slate-500">No insurance claims found</p>
              <p className="text-sm text-slate-400">Claims will appear here when customers file them</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-slate-50 border-b border-slate-200">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase">Claim ID</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase">Customer</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase">Type</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase">Amount</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase">Status</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase">Date</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {filteredClaims.map((claim) => (
                    <tr key={claim.id} className="hover:bg-slate-50" data-testid={`claim-row-${claim.id}`}>
                      <td className="px-4 py-3">
                        <p className="text-sm font-medium text-slate-900">{claim.id?.slice(0, 8)}...</p>
                        <p className="text-xs text-slate-500">Booking: {claim.booking_id?.slice(0, 8)}...</p>
                      </td>
                      <td className="px-4 py-3">
                        <p className="text-sm text-slate-900">{claim.customer_name || 'Unknown'}</p>
                      </td>
                      <td className="px-4 py-3">
                        {getClaimTypeBadge(claim.claim_type)}
                      </td>
                      <td className="px-4 py-3">
                        <p className="text-sm font-medium text-slate-900">₱{(claim.estimated_damage || 0).toLocaleString()}</p>
                        <p className="text-xs text-slate-500">Limit: ₱{(claim.coverage_limit || 0).toLocaleString()}</p>
                      </td>
                      <td className="px-4 py-3">
                        {getStatusBadge(claim.status)}
                      </td>
                      <td className="px-4 py-3">
                        <p className="text-sm text-slate-600">{new Date(claim.created_at).toLocaleDateString()}</p>
                        <p className="text-xs text-slate-400">{new Date(claim.created_at).toLocaleTimeString()}</p>
                      </td>
                      <td className="px-4 py-3">
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => {
                            setSelectedClaim(claim);
                            setAdminNotes(claim.admin_notes || '');
                            setApprovedAmount(claim.approved_amount?.toString() || claim.estimated_damage?.toString() || '');
                            setShowDetailDialog(true);
                          }}
                          data-testid={`view-claim-${claim.id}`}
                        >
                          <Eye className="w-4 h-4 mr-1" />
                          Review
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
        </div>
      </main>

      {/* Claim Detail Dialog */}
      <Dialog open={showDetailDialog} onOpenChange={setShowDetailDialog}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Shield className="w-5 h-5 text-blue-600" />
              Review Insurance Claim
            </DialogTitle>
          </DialogHeader>
          
          {selectedClaim && (
            <div className="space-y-4 mt-4">
              {/* Claim Info */}
              <div className="grid grid-cols-2 gap-4">
                <div className="bg-slate-50 rounded-lg p-3">
                  <p className="text-xs text-slate-500 flex items-center gap-1"><User className="w-3 h-3" /> Customer</p>
                  <p className="font-medium text-slate-900">{selectedClaim.customer_name}</p>
                </div>
                <div className="bg-slate-50 rounded-lg p-3">
                  <p className="text-xs text-slate-500 flex items-center gap-1"><Calendar className="w-3 h-3" /> Filed</p>
                  <p className="font-medium text-slate-900">{new Date(selectedClaim.created_at).toLocaleDateString()}</p>
                </div>
              </div>

              {/* Claim Type & Amount */}
              <div className="grid grid-cols-2 gap-4">
                <div className="bg-slate-50 rounded-lg p-3">
                  <p className="text-xs text-slate-500 flex items-center gap-1"><AlertTriangle className="w-3 h-3" /> Claim Type</p>
                  <p className="font-medium text-slate-900">{selectedClaim.claim_type?.replace('_', ' ')}</p>
                </div>
                <div className="bg-slate-50 rounded-lg p-3">
                  <p className="text-xs text-slate-500 flex items-center gap-1"><DollarSign className="w-3 h-3" /> Claimed Amount</p>
                  <p className="font-medium text-slate-900">₱{(selectedClaim.estimated_damage || 0).toLocaleString()}</p>
                  <p className="text-xs text-slate-500">Coverage: ₱{(selectedClaim.coverage_limit || 0).toLocaleString()}</p>
                </div>
              </div>

              {/* Description */}
              <div className="bg-slate-50 rounded-lg p-3">
                <p className="text-xs text-slate-500 flex items-center gap-1 mb-1"><FileText className="w-3 h-3" /> Description</p>
                <p className="text-sm text-slate-700">{selectedClaim.description || 'No description provided'}</p>
              </div>

              {/* Current Status */}
              <div className="flex items-center justify-between bg-blue-50 rounded-lg p-3">
                <span className="text-sm text-blue-700">Current Status:</span>
                {getStatusBadge(selectedClaim.status)}
              </div>

              {/* Admin Actions */}
              {selectedClaim.status !== 'approved' && selectedClaim.status !== 'rejected' && (
                <>
                  {/* Approved Amount */}
                  <div>
                    <label className="text-sm font-medium text-slate-700">Approved Amount (₱)</label>
                    <Input
                      type="number"
                      value={approvedAmount}
                      onChange={(e) => setApprovedAmount(e.target.value)}
                      placeholder="Enter amount to approve"
                      className="mt-1"
                      max={selectedClaim.coverage_limit}
                      data-testid="approved-amount-input"
                    />
                    <p className="text-xs text-slate-500 mt-1">Max: ₱{(selectedClaim.coverage_limit || 0).toLocaleString()}</p>
                  </div>

                  {/* Admin Notes */}
                  <div>
                    <label className="text-sm font-medium text-slate-700 flex items-center gap-1">
                      <MessageSquare className="w-3 h-3" /> Admin Notes
                    </label>
                    <Textarea
                      value={adminNotes}
                      onChange={(e) => setAdminNotes(e.target.value)}
                      placeholder="Add notes about this claim decision..."
                      className="mt-1 min-h-[80px]"
                      data-testid="admin-notes-input"
                    />
                  </div>

                  {/* Action Buttons */}
                  <div className="flex gap-2 pt-2">
                    {selectedClaim.status === 'pending' && (
                      <Button
                        className="flex-1 bg-blue-600 hover:bg-blue-700"
                        onClick={() => handleUpdateStatus(selectedClaim.id, 'under_review')}
                        disabled={isUpdating}
                        data-testid="mark-reviewing-btn"
                      >
                        <Eye className="w-4 h-4 mr-2" />
                        Mark as Reviewing
                      </Button>
                    )}
                    <Button
                      className="flex-1 bg-green-600 hover:bg-green-700"
                      onClick={() => handleUpdateStatus(selectedClaim.id, 'approved')}
                      disabled={isUpdating}
                      data-testid="approve-btn"
                    >
                      <CheckCircle className="w-4 h-4 mr-2" />
                      Approve
                    </Button>
                    <Button
                      className="flex-1 bg-red-600 hover:bg-red-700"
                      onClick={() => handleUpdateStatus(selectedClaim.id, 'rejected')}
                      disabled={isUpdating}
                      data-testid="reject-btn"
                    >
                      <XCircle className="w-4 h-4 mr-2" />
                      Reject
                    </Button>
                  </div>
                </>
              )}

              {/* Show admin notes if already processed */}
              {(selectedClaim.status === 'approved' || selectedClaim.status === 'rejected') && (
                <div className="bg-slate-100 rounded-lg p-3">
                  <p className="text-xs text-slate-500 mb-1">Decision Notes:</p>
                  <p className="text-sm text-slate-700">{selectedClaim.admin_notes || 'No notes'}</p>
                  {selectedClaim.approved_amount && (
                    <p className="text-sm font-medium text-green-700 mt-2">
                      Approved: ₱{selectedClaim.approved_amount.toLocaleString()}
                    </p>
                  )}
                  <p className="text-xs text-slate-500 mt-2">
                    Reviewed by: {selectedClaim.reviewer_email || 'Unknown'}
                  </p>
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
